import {cloneDeep} from '@shared/helpers/lang'
import {createKey} from '@shared/helpers/object'

import {AccumulateClassTypeType, APIStatus, AccumulateClassType} from '@web/core/constants'

import {
    PurchaseRewardPoint,
    PurchaseSubTypeType,
    ReviewSubType,
    ReviewSubTypeType,
    RewardState,
    OrderSheetItemId,
    PurchaseRewardPointsByItemId,
    LogeyeRewardPointListsByItemId,
    ReviewRewardPointsByItemId,
} from '../interfaces'

export const getInitialPurchaseRewardPointObject = (): PurchaseRewardPoint => ({
    purchase: 0,
    merchant: 0,
    sellerCustomerManagementByTitle: {},
    adminAdditional: 0,
    power: 0,
    chargePoint: 0,
    naverBankbook: 0,
    membership: 0,
    arrivalGuarantee: 0,
    arrivalGuaranteeMembership: 0,
    expectedNaverBankbook: 0,
    expectedChargePoint: 0,
    logeyeRewardPointByTitle: {},
    totalRewardPoint: 0,
})

export const getInitialPurchaseTotalRewardPointObject = () => ({
    purchase: {title: '기본적립', amount: 0},
    merchant: {title: '판매자 추가구매적립', amount: 0},
    sellerCustomerManagementByTitle: {},
    adminAdditional: {
        title: '상품구매 추가적립',
        amount: 0,
    },
    power: {title: '파워적립', amount: 0},
    chargePoint: {title: '네이버페이 머니 결제적립', amount: 0},
    naverBankbook: {title: '네이버통장 추가적립', amount: 0},
    membership: {title: '멤버십 추가적립', amount: 0},
    arrivalGuarantee: {title: '네이버도착보장 추가적립', amount: 0},
    arrivalGuaranteeMembership: {title: '멤버십 네이버도착보장 추가적립', amount: 0},
    expectedNaverBankbook: 0,
    expectedChargePoint: 0,
    logeyeRewardPointByTitle: {},
    totalRewardPoint: 0,
    scheduledSellerBurden: {title: '렌탈료 정기결제 추가적립', amount: 0},
})

export function createInitialPurchaseRewardPoint(): RewardState['purchaseRewardPoint'] {
    return {
        pointsByOrderSheetItemId: {},
        total: getInitialPurchaseTotalRewardPointObject(),
        pointsByProductOrderNo: {},
    }
}

export function createInitialReviewRewardPoint(): RewardState['reviewRewardPoint'] {
    return {
        pointsByOrderSheetItemId: {},
        total: {
            afterUsePhotoVideoReview: 0,
            afterUseTextReview: 0,
            maxRewardPoint: 0,
            totalRewardPoint: 0,
            writeTextReview: 0,
            writePhotoVideoReview: 0,
            subscriberReview: 0,
        },
        pointsByProductOrderNo: {},
    }
}

function createInitialEasyBookingRewardPoint(): RewardState['easyBookingRewardPoint'] {
    return {
        total: 0,
        pointsByTitle: {},
    }
}

export const getInitialState = (): RewardState => ({
    apiStatus: {
        PURCHASE: APIStatus.IDLE,
        REVIEW: APIStatus.IDLE,
        BENEFIT_CARD: APIStatus.IDLE,
        EXPECTED_MEMBERSHIP: APIStatus.IDLE,
        EASY_BOOKING: APIStatus.IDLE,
        EVENT: APIStatus.IDLE,
    },
    items: [],
    cardEasyPayRewardPoint: 0,
    purchaseRewardPoint: createInitialPurchaseRewardPoint(),
    reviewRewardPoint: createInitialReviewRewardPoint(),
    easyBookingRewardPoint: createInitialEasyBookingRewardPoint(),
    totalExpectedPurchaseRewardPoint: 0,
    expectedChargePoint: 0,
    expectedNaverBankbook: 0,
    products: [],
    expectedMembershipPointAmount: 0,
    benefitCardRewardTid: null,
    events: [],
})

export const isReviewSubType = (subType: PurchaseSubTypeType | ReviewSubTypeType): subType is ReviewSubTypeType => {
    return subType in ReviewSubType
}

/**
 * @check
 * - 혜택쪽에서 리뷰적립 유형에 대해 별도 type 추가가 어려워서, 필드명을 보고 타입을 직접 지정해주어야 하는 상황
 */
export const ReviewItemSubTypeMap: Record<string, ReviewSubTypeType> = {
    productTextReviewAcmAmount: ReviewSubType.WRITE_TEXT_REVIEW,
    productPhotoVideoReviewAcmAmount: ReviewSubType.WRITE_PHOTO_VIDEO_REVIEW,
    afterUseTextReviewAcmAmount: ReviewSubType.AFTER_USE_TEXT_REVIEW,
    afterUsePhotoVideoReviewAcmAmount: ReviewSubType.AFTER_USE_PHOTO_VIDEO_REVIEW,
    customerAddReviewAcmAmount: ReviewSubType.SUBSCRIBER_REVIEW,
    afterUseReviewMaxAcmAmount: ReviewSubType.AFTER_USE_WRITE_TOTAL,
    productReviewMaxAcmAmount: ReviewSubType.WRITE_TOTAL,
    reviewMaxAcmAmount: ReviewSubType.REVIEW_TOTAL,
    WRITE_REVIEW: ReviewSubType.WRITTEN_WRITE_TEXT_REVIEW,
    WRITE_PREMIUM_REVIEW: ReviewSubType.WRITTEN_WRITE_PHOTO_VIDEO_REVIEW,
    ADD_CUSTOM_WRITE_REVIEW: ReviewSubType.WRITTEN_SUBSCRIBER_REVIEW,
    AFTER_USE_WRITE_REVIEW: ReviewSubType.WRITTEN_AFTER_USE_TEXT_REVIEW,
    AFTER_USE_WRITE_PREMIUM_REVIEW: ReviewSubType.WRITTEN_AFTER_USE_PHOTO_VIDEO_REVIEW,
}

export const refinePurchaseRewardPoint = (
    purchaseRewardPointsByItemId: Record<OrderSheetItemId, PurchaseRewardPointsByItemId>,
    logeyeRewardPointListsByItemId: Record<OrderSheetItemId, LogeyeRewardPointListsByItemId>,
) => {
    const purchaseRewardPoint = createInitialPurchaseRewardPoint()
    Object.entries(purchaseRewardPointsByItemId).forEach(([itemId, purchaseRewardPointByItemId]) => {
        purchaseRewardPoint.pointsByOrderSheetItemId[itemId] = {
            purchase: purchaseRewardPointByItemId.admin,
            merchant: purchaseRewardPointByItemId.seller,
            sellerCustomerManagementByTitle: purchaseRewardPointByItemId.sellerCustomerManagementByTitle ?? {},
            logeyeRewardPointByTitle: {},
            adminAdditional: purchaseRewardPointByItemId.adminAdditional,
            power: purchaseRewardPointByItemId.power,
            chargePoint: purchaseRewardPointByItemId.chargePoint,
            naverBankbook: purchaseRewardPointByItemId.naverBankbook,
            membership: purchaseRewardPointByItemId.membership ?? 0,
            arrivalGuarantee: purchaseRewardPointByItemId.arrivalGuarantee ?? 0,
            arrivalGuaranteeMembership: purchaseRewardPointByItemId.arrivalGuaranteeMembership ?? 0,
            expectedNaverBankbook: 0,
            expectedChargePoint: 0,
            totalRewardPoint: 0,
        }
        if (logeyeRewardPointListsByItemId && itemId in logeyeRewardPointListsByItemId) {
            const logeyeRewardPointList = logeyeRewardPointListsByItemId[itemId]
            logeyeRewardPointList?.forEach(({policyName, rewardPointAmount, membershipReward}) => {
                if (purchaseRewardPoint.pointsByOrderSheetItemId[itemId]) {
                    purchaseRewardPoint.pointsByOrderSheetItemId[itemId].logeyeRewardPointByTitle[policyName] = {
                        rewardAmount: rewardPointAmount,
                        membershipReward,
                    }
                }
            })
        }
    })

    return purchaseRewardPoint
}

export const refineReviewRewardPoint = (
    reviewRewardPointsByItemId: Record<OrderSheetItemId, ReviewRewardPointsByItemId>,
) => {
    const reviewRewardPoint = createInitialReviewRewardPoint()

    for (const [key, reviewRewardPointByItemId] of Object.entries(reviewRewardPointsByItemId)) {
        reviewRewardPoint.pointsByOrderSheetItemId[key] = {
            afterUsePhotoVideoReview: reviewRewardPointByItemId.afterUsePhotoVideoReview,
            afterUseTextReview: reviewRewardPointByItemId.afterUseTextReview,
            writePhotoVideoReview: reviewRewardPointByItemId.writePhotoVideoReview,
            writeTextReview: reviewRewardPointByItemId.writeTextReview,
            subscriberReview: reviewRewardPointByItemId.subscriberReview,
            maxRewardPoint: reviewRewardPointByItemId.maxRewardPoint,
            totalRewardPoint: 0,
        }
    }

    return reviewRewardPoint
}

interface DiconRewardItem {
    cpId: string
    itemId: string
    /** 총 결제 금액 */
    payAmount: number
    /** 적립 기준 금액 */
    acmBasisAmount: number
    /** 비동기 적립 적립예상일 */
    confirmExpectDays: number
    /** 적립 정보 */
    acmInfos: {
        /** 적립 유형 */
        accumulateClassType: Extract<
            AccumulateClassTypeType,
            'DIGITAL_CONTENTS_ACM' | 'CHARGE_ADD_ACM' | 'BANKBOOK_ACM'
        >
        acmAmount: number
        expectedAcmAmount: number
        acmRate: Maybe<number>
        acmName: Maybe<string>
    }[]
}

/** @CHECK 주문형 혜택 API 업데이트 될 경우 위치 수정 */
const AccumulateClassTypeInfo: {
    [key: string]: keyof Pick<PurchaseRewardPoint, 'purchase' | 'chargePoint' | 'naverBankbook'>
} = {
    [AccumulateClassType.DIGITAL_CONTENTS_ACM]: 'purchase',
    [AccumulateClassType.CHARGE_ADD_ACM]: 'chargePoint',
    [AccumulateClassType.BANKBOOK_ACM]: 'naverBankbook',
} as const

const createItemKey = createKey('itemId')

export const convertExpectRewardInfo = (prevState: RewardState, items: Array<DiconRewardItem>) => {
    const state = cloneDeep(prevState)

    /** 전체 아이템의 혜택 정보로부터 store의 key - 적립명 정보 생성 */
    const acmClassTypeInfo = items.reduce<{
        [key: string]: string
    }>((acc, item) => {
        item.acmInfos.forEach((acmInfo) => {
            const key = AccumulateClassTypeInfo[acmInfo.accumulateClassType]
            if (acmInfo.accumulateClassType && key) {
                return (acc[key] = acmInfo.acmName ?? '')
            }
            return acc
        })
        return acc
    }, {})

    /**
     * @CHECK
     * - 디컨에서 사용되는 적립유형 정보로 store값 반영
     * - 주문형 혜택 API 업데이트 될 경우 title 초기화 위치 수정 */
    Object.entries(acmClassTypeInfo).forEach(([originKey, title]) => {
        const key = originKey as keyof Pick<PurchaseRewardPoint, 'purchase' | 'chargePoint' | 'naverBankbook'>
        state.purchaseRewardPoint.total[key] = {
            amount: state.purchaseRewardPoint.total[key].amount,
            title,
        }
    })

    const accInfoTypes = [
        AccumulateClassType.DIGITAL_CONTENTS_ACM,
        AccumulateClassType.BANKBOOK_ACM,
        AccumulateClassType.CHARGE_ADD_ACM,
    ]

    /** 상품별 적립정보 저장 */
    state.purchaseRewardPoint.pointsByOrderSheetItemId = items.reduce((acc, item, index) => {
        const accAmountMap = item.acmInfos.reduce(
            (itemAcc, acmInfo) => {
                if (acmInfo.accumulateClassType && accInfoTypes.includes(acmInfo.accumulateClassType)) {
                    itemAcc[acmInfo.accumulateClassType] = acmInfo.acmAmount
                }
                return itemAcc
            },
            {
                [AccumulateClassType.DIGITAL_CONTENTS_ACM]: 0,
                [AccumulateClassType.BANKBOOK_ACM]: 0,
                [AccumulateClassType.CHARGE_ADD_ACM]: 0,
            },
        )

        return {
            ...acc,
            [createItemKey(index)]: {
                ...state.purchaseRewardPoint.pointsByOrderSheetItemId[createItemKey(index)],
                purchase: accAmountMap[AccumulateClassType.DIGITAL_CONTENTS_ACM],
                chargePoint: accAmountMap[AccumulateClassType.CHARGE_ADD_ACM],
                naverBankbook: accAmountMap[AccumulateClassType.BANKBOOK_ACM],
            },
        }
    }, {})

    // 네이버 통장 적립금
    state.expectedNaverBankbook = items.reduce((acc, item) => {
        const bankBookInfo = item.acmInfos.find(
            (acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.BANKBOOK_ACM,
        )
        return acc + (bankBookInfo ? bankBookInfo?.expectedAcmAmount : 0)
    }, 0)

    // 머니 결제 적립금
    state.expectedChargePoint = items.reduce((acc, item) => {
        const chargePointInfo = item.acmInfos.find(
            (acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.CHARGE_ADD_ACM,
        )
        return acc + (chargePointInfo ? chargePointInfo?.expectedAcmAmount : 0)
    }, 0)

    if (items[0]) {
        state.purchaseRewardPoint.dateDelay = items.reduce((maxDelay, item) => {
            return Math.max(maxDelay, item.confirmExpectDays)
        }, items[0].confirmExpectDays)
    }

    return state
}
